/* eslint-disable max-classes-per-file */
import { isPlainObject } from 'lodash';

// Named so the offer gate can report which way the dag is broken.
export class DagCycleError extends Error {
  constructor() {
    super('strategy dag contains a cycle');
    this.name = 'DagCycleError';
  }
}

export class MissingImplementationError extends Error {
  constructor(key) {
    super(`missing strategy implementation: ${key}`);
    this.name = 'MissingImplementationError';
    this.key = key;
  }
}

/** One dag level as [stepKey, innerDag] pairs. Empty arrives as [] from PHP as well as {}. */
const dagEntries = (dag) => (isPlainObject(dag) ? Object.entries(dag) : []);

/**
 * Leaves-first topological order of a chameleonDag (Kahn's algorithm). A step
 * nests the steps that must run before it, so a step is emitted only once every
 * step before it has been. Mirrors Rhino's dagToExecutionOrder so both sides
 * agree on the walk.
 */
export const getDagExecutionOrder = (chameleonDag) => {
  const stepsBefore = new Map();
  const stepsAfter = new Map();
  const getOrAddSteps = (map, stepKey) => {
    if (!map.has(stepKey)) map.set(stepKey, new Set());
    return map.get(stepKey);
  };

  const readDag = (dag) => {
    dagEntries(dag).forEach(([stepKey, innerDag]) => {
      const earlierSteps = getOrAddSteps(stepsBefore, stepKey);
      getOrAddSteps(stepsAfter, stepKey);
      dagEntries(innerDag).forEach(([earlierStepKey]) => {
        getOrAddSteps(stepsBefore, earlierStepKey);
        earlierSteps.add(earlierStepKey);
        getOrAddSteps(stepsAfter, earlierStepKey).add(stepKey);
      });
      readDag(innerDag);
    });
  };
  readDag(chameleonDag);

  const remainingSteps = new Map();
  const readySteps = [];
  stepsBefore.forEach((earlierSteps, stepKey) => {
    remainingSteps.set(stepKey, earlierSteps.size);
    if (earlierSteps.size === 0) readySteps.push(stepKey);
  });

  const executionOrder = [];
  while (readySteps.length > 0) {
    const stepKey = readySteps.shift();
    executionOrder.push(stepKey);
    (stepsAfter.get(stepKey) ?? []).forEach((laterStepKey) => {
      const remaining = remainingSteps.get(laterStepKey) - 1;
      remainingSteps.set(laterStepKey, remaining);
      if (remaining === 0) readySteps.push(laterStepKey);
    });
  }

  if (executionOrder.length !== stepsBefore.size) throw new DagCycleError();

  return executionOrder;
};

/**
 * The collection's journey as ordered steps, each key paired with its
 * implementation. A key the dag names but the collection does not implement is
 * a broken collection, not a step to skip.
 */
export const getJourneySteps = (collection) =>
  getDagExecutionOrder(collection?.chameleonDag).map((key) => {
    const implementation = collection?.implementations?.[key];
    if (!implementation) throw new MissingImplementationError(key);
    return { key, implementation };
  });
